// Lightweight IndexedDB-backed queue for checkpoint scans captured while offline.
// Survives reload, dedupes by clientId, flushes when connectivity returns.
//
// v2 schema: adds clientId (server-side idempotency), nextRetryAt (exponential backoff),
// silentPanic, and drops the legacy "nfc" method.

const DB_NAME = "guard-tour-offline";
const STORE = "scan_queue";
const DB_VERSION = 2;

export type QueuedScanMethod = "qr_camera" | "qr_image" | "manual" | "gps_auto";

export type QueuedScan = {
  id: string;            // local uuid (also used as clientId for server-side dedupe)
  token?: string;
  checkpointId?: string;
  method: QueuedScanMethod;
  capturedAt: string;    // ISO timestamp from when officer scanned
  latitude?: number | null;
  longitude?: number | null;
  accuracy?: number | null;
  note?: string | null;
  silentPanic?: boolean;
  attempts: number;
  nextRetryAt?: number;  // epoch ms — earliest time to retry (backoff)
  lastError?: string;
};

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T> | Promise<T>): Promise<T> {
  const db = await openDb();
  return new Promise<T>((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const store = t.objectStore(STORE);
    const r = fn(store);
    if (r instanceof IDBRequest) {
      r.onsuccess = () => resolve(r.result as T);
      r.onerror = () => reject(r.error);
    } else {
      r.then(resolve, reject);
    }
  });
}

export async function enqueueScan(item: Omit<QueuedScan, "id" | "attempts">): Promise<QueuedScan> {
  const queued: QueuedScan = {
    ...item,
    id: crypto.randomUUID(),
    attempts: 0,
  };
  await tx("readwrite", (s) => s.put(queued));
  return queued;
}

export async function listQueuedScans(): Promise<QueuedScan[]> {
  return tx<QueuedScan[]>("readonly", (s) =>
    new Promise<any>((resolve) => {
      const out: QueuedScan[] = [];
      const req = s.openCursor();
      req.onsuccess = () => {
        const cur = req.result;
        if (cur) { out.push(cur.value); cur.continue(); } else resolve(out);
      };
    }) as any,
  );
}

export async function removeQueuedScan(id: string): Promise<void> {
  await tx("readwrite", (s) => s.delete(id));
}

export async function updateQueuedScan(item: QueuedScan): Promise<void> {
  await tx("readwrite", (s) => s.put(item));
}

export async function queueSize(): Promise<number> {
  return tx<number>("readonly", (s) => s.count());
}

/** Exponential backoff: 5s, 15s, 45s, 2m, 5m, 15m (capped). */
export function computeBackoffMs(attempts: number): number {
  const base = 5000;
  const ms = base * Math.pow(3, Math.min(attempts, 5));
  return Math.min(ms, 15 * 60 * 1000);
}
